import { Loader2Icon } from "lucide-react";
import { useState } from "react";
import { useWaitedLong } from "../../lib/motion";
import { cn } from "../../lib/utils";

/** Stałe klucze kropek — indeks tablicy nie nadaje się na klucz. */
const DOTS = ["a", "b", "c"] as const;

/**
 * Wczytywanie całego ekranu — tam, gdzie nie ma jeszcze kształtu do pokazania
 * (sesja, doładowywany plik ekranu). Gdzie kształt jest znany, lepszy jest
 * szkielet (`Skeleton`), bo układ nie skacze po wczytaniu.
 *
 * Pojawia się z opóźnieniem (`styles.css`), więc szybka odpowiedź nie błyska
 * kropkami. `<output>` ma rolę `status` — czytnik ekranu dostaje podpis, kropki są ozdobą.
 */
export function PageLoading({ label = "Wczytuję…" }: { label?: string }) {
  return (
    <output
      data-slot="page-loading"
      className="flex min-h-[40svh] flex-col items-center justify-center gap-4 text-sm text-muted-foreground"
    >
      <span aria-hidden="true" className="flex gap-1.5">
        {DOTS.map((dot) => (
          <span
            key={dot}
            data-slot="loading-dot"
            className="size-2.5 rounded-full bg-gradient-to-br from-primary to-accent"
          />
        ))}
      </span>
      {label}
    </output>
  );
}

/**
 * Zdanie dla czekania na źródła — to samo w liście odcinków, oknie wersji i kolejce
 * pokoju. Mówi PRAWDĘ o przyczynie: Shinden pytamy co kilkanaście sekund, bo przy
 * szybszych wejściach oddaje pustą tabelę kopii (CLAUDE.md, „Shinden: tempo jest
 * częścią protokołu").
 */
export const SLOW_SOURCES_LABEL =
  "Źródła odpowiadają dłużej niż zwykle — część z nich pytamy powoli, żeby nas nie zablokowały.";

/**
 * Wczytywanie w jednej linii: kręciołek i zdanie, po kilku sekundach drugie
 * zdanie z wyjaśnieniem.
 *
 * Powstało ze zgłoszenia z 12.10.2026: okno wersji odcinka przez pół minuty
 * pytania źródeł pokazywało tylko napis „Pytam źródła…" — nieruchomy, czyli
 * nie do odróżnienia od zawieszenia. `since` podaje się wtedy, gdy wskaźnik
 * może zamontować się drugi raz w trakcie tego samego czekania.
 */
export function LoadingNote({
  label,
  slowLabel,
  since,
  className,
}: {
  label: string;
  slowLabel?: string;
  since?: number;
  className?: string;
}) {
  const [mountedAt] = useState(() => Date.now());
  const slow = useWaitedLong(since ?? mountedAt);
  return (
    <output className={cn("flex items-center gap-2 text-sm text-muted-foreground", className)}>
      <Loader2Icon aria-hidden="true" className="size-4 shrink-0 animate-spin text-accent" />
      <span>{slow && slowLabel !== undefined ? slowLabel : label}</span>
    </output>
  );
}
